import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ThemeProvider, useTheme } from './hooks/useTheme';
import { useDarkMode } from './hooks/useDarkMode';
import { AnimatePresence } from 'framer-motion';
import BottomNav from './components/BottomNav';
import ErrorBoundary from './components/ErrorBoundary';
import BrandLockup from './components/BrandMark';
import { BRAND_NAME } from './constants/brand';
import ConsentGate from './components/ConsentGate';
import InstallHint from './components/InstallHint';

// Pages — 懒加载，减小首屏 bundle
const Login = lazy(() => import('./pages/Login'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const Home = lazy(() => import('./pages/Home'));
const Chat = lazy(() => import('./pages/Chat'));
const CircleDetail = lazy(() => import('./pages/CircleDetail'));
const CircleEase = lazy(() => import('./pages/CircleEase'));
const FavorLedger = lazy(() => import('./pages/FavorLedger'));
const LifePacks = lazy(() => import('./pages/LifePacks'));
const FamilyPlan = lazy(() => import('./pages/FamilyPlan'));
const Friends = lazy(() => import('./pages/Friends'));
const Profile = lazy(() => import('./pages/Profile'));
const Messages = lazy(() => import('./pages/Messages'));
const SharedList = lazy(() => import('./pages/SharedList'));
const Moments = lazy(() => import('./pages/Moments'));
const Polls = lazy(() => import('./pages/Polls'));
const Widget = lazy(() => import('./pages/Widget'));
const Invite = lazy(() => import('./pages/Invite'));
const Decentralized = lazy(() => import('./pages/Decentralized'));
const RelationshipHealth = lazy(() => import('./pages/RelationshipHealth'));
const GrowthHub = lazy(() => import('./pages/GrowthHub'));
const TimeCapsule = lazy(() => import('./pages/TimeCapsule'));
const CircleStickers = lazy(() => import('./pages/CircleStickers'));
const HealthAI = lazy(() => import('./pages/HealthAI'));
const WechatImport = lazy(() => import('./pages/WechatImport'));
const Album = lazy(() => import('./pages/Album'));
const Anniversaries = lazy(() => import('./pages/Anniversaries'));
const KinshipHub = lazy(() => import('./pages/KinshipHub'));
const JoinInvite = lazy(() => import('./pages/JoinInvite'));
const PublicShare = lazy(() => import('./pages/PublicShare'));
const Waitlist = lazy(() => import('./pages/Waitlist'));
const FounderDashboard = lazy(() => import('./pages/FounderDashboard'));
const Landing = lazy(() => import('./pages/Landing'));
const LegalDoc = lazy(() => import('./pages/LegalDoc'));
const ElderOnboard = lazy(() => import('./pages/ElderOnboard'));

// 懒加载 fallback
const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-dark-bg transition-colors duration-300">
    <div className="text-center">
      <div className="mb-4 flex justify-center">
        <BrandLockup size="md" tone="primary" />
      </div>
      <p className="text-neutral-400 dark:text-dark-muted text-sm">加载中...</p>
    </div>
  </div>
);

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-dark-bg transition-colors duration-300">
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <BrandLockup size="md" tone="primary" />
          </div>
          <p className="text-neutral-400 dark:text-dark-muted text-sm">加载中...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // 首登强制改密门禁 —— 只在这一处判。
  //
  // 判定依据是服务端下发的 mustChangePassword（/me 与登录响应都会带），
  // 不是本地自记的标记：本地那份只在登录那一刻写过，带着旧 token 进来的会话
  // 本地是 false，门禁就形同虚设。
  //
  // 放在 ProtectedRoute 里而不是逐个页面加，是因为受保护路由有 30 多条 ——
  // 逐页加必然漏，而漏掉的那一条不会有任何测试喊出来。
  if (user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return (
    <ConsentGate>
      {/* iOS 从浏览器进入时的一次性「放到桌面」提示：家人被邀请链接带进来一次，
          没有桌面图标就等于没有入口。只对 iOS + 未安装 + 没关过显示一次。 */}
      <InstallHint />
      {children}
      <BottomNav />
    </ConsentGate>
  );
}

/** 仅 FOUNDER_PHONES 白名单可见 */
function FounderRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-dark-bg">
        <BrandLockup size="md" tone="primary" />
      </div>
    );
  }
  if (!user?.isFounder) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppInit() {
  useDarkMode();

  const { user, loading } = useAuth();
  const { syncElderFromServer } = useTheme();

  useEffect(() => {
    if (user) syncElderFromServer(user);
  }, [user, syncElderFromServer]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-dark-bg transition-colors duration-300">
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <BrandLockup size="md" tone="primary" />
          </div>
          <p className="text-neutral-500 dark:text-dark-muted text-sm font-display">{BRAND_NAME}加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<PageFallback />}>
        <Routes>
        {/* Public：产品落地页为未登录根路径 */}
        <Route path="/" element={user ? <ProtectedRoute><Home /></ProtectedRoute> : <Landing />} />
        <Route path="/landing" element={user ? <Navigate to="/" replace /> : <Landing />} />
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/i/:code" element={<JoinInvite />} />
        <Route path="/join/:code" element={<JoinInvite />} />
        <Route path="/share/:type/:token" element={<PublicShare />} />
        <Route path="/waitlist" element={<Waitlist />} />
        <Route path="/legal/:docType" element={<LegalDoc />} />
        <Route path="/elder/onboard/:token" element={<ElderOnboard />} />

        {/* Protected */}
        {/* 改密页：首登强制改密（ProtectedRoute 会把人送到这里）与主动改密共用同一个页面 */}
        <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
        <Route path="/chat/:userId" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
        <Route path="/circle/:circleId/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
        <Route path="/circle/:id" element={<ProtectedRoute><CircleDetail /></ProtectedRoute>} />
        <Route path="/circle/:id/ease" element={<ProtectedRoute><CircleEase /></ProtectedRoute>} />
        <Route path="/circle/:circleId/favors" element={<ProtectedRoute><FavorLedger /></ProtectedRoute>} />
        <Route path="/circle/:circleId/life-packs" element={<ProtectedRoute><LifePacks /></ProtectedRoute>} />
        <Route path="/family-plan" element={<ProtectedRoute><FamilyPlan /></ProtectedRoute>} />
        <Route path="/circle/:circleId/lists" element={<ProtectedRoute><SharedList /></ProtectedRoute>} />
        <Route path="/circle/:circleId/moments" element={<ProtectedRoute><Moments /></ProtectedRoute>} />
        <Route path="/circle/:circleId/polls" element={<ProtectedRoute><Polls /></ProtectedRoute>} />
        <Route path="/friends" element={<ProtectedRoute><Friends /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/widget" element={<ProtectedRoute><Widget /></ProtectedRoute>} />
        <Route path="/invite" element={<ProtectedRoute><Invite /></ProtectedRoute>} />
        <Route path="/decentralized" element={<ProtectedRoute><Decentralized /></ProtectedRoute>} />
        <Route path="/relationship-health" element={<ProtectedRoute><RelationshipHealth /></ProtectedRoute>} />
        <Route path="/growth-hub" element={<ProtectedRoute><GrowthHub /></ProtectedRoute>} />
        <Route
          path="/founder"
          element={
            <ProtectedRoute>
              <FounderRoute>
                <FounderDashboard />
              </FounderRoute>
            </ProtectedRoute>
          }
        />
        <Route path="/circle/:circleId/capsules" element={<ProtectedRoute><TimeCapsule /></ProtectedRoute>} />
        <Route path="/circle/:circleId/stickers" element={<ProtectedRoute><CircleStickers /></ProtectedRoute>} />
        <Route path="/health-ai" element={<ProtectedRoute><HealthAI /></ProtectedRoute>} />
        <Route path="/local-ai" element={<Navigate to="/health-ai" replace />} />
        <Route path="/wechat-import" element={<ProtectedRoute><WechatImport /></ProtectedRoute>} />
        <Route path="/circle/:circleId/album" element={<ProtectedRoute><Album /></ProtectedRoute>} />
        <Route path="/circle/:circleId/anniversaries" element={<ProtectedRoute><Anniversaries /></ProtectedRoute>} />
        <Route path="/circle/:circleId/kinship" element={<ProtectedRoute><KinshipHub /></ProtectedRoute>} />
        <Route path="/chat/:userId/album" element={<ProtectedRoute><Album /></ProtectedRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <ErrorBoundary
      title="朋际遇到了一些问题"
      message="应用遇到了意外错误，请尝试刷新页面。如果问题持续存在，请联系我们。"
      supportEmail="app@cdcn.com"
    >
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <AppInit />
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
